/*
 * Appointment drag state (v1.0-P1.1).
 * Root cause of the "second drag impossible" defect: the drop handler re-rendered the calendar synchronously,
 * destroying the dragged card before the browser fired `dragend` on it. A `dragend` dispatched on a detached node
 * never bubbles to `document`, so the cleanup listener never ran: `draggedId` stayed set and `body.is-dragging`
 * (which sets `pointer-events:none` on every other card) stayed on — the calendar looked frozen and no new drag
 * could start until a full reload. `dragover` also cleared/re-added target classes on every mouse move.
 *
 * This controller owns the whole lifecycle so cleanup happens on drop itself (before a re-render), is idempotent,
 * and every exit path (success, invalid, rejected, stale version, cancelled, escape) returns to IDLE. Visual
 * feedback is diffed: DOM classes change only when the hovered column or its validity changes.
 */
export type DragPhase = 'idle' | 'dragging' | 'dropping';

export interface DragHost {
  /* Marks/unmarks the whole document as dragging (body.is-dragging). */
  setBodyDragging(on: boolean): void;
  /* Applies the source-card highlight; `null` clears the previous card. */
  setDraggingCard(cardKey: string | null): void;
  /* Applies the hovered column feedback; `null` clears the previous column. */
  setTarget(columnKey: string | null, valid: boolean): void;
}

export interface DragSnapshot { phase: DragPhase; appointmentId: string | null; cardKey: string | null; targetKey: string | null; targetValid: boolean | null; drops: number; }

export class AppointmentDragController {
  private phase: DragPhase = 'idle';
  private appointmentId: string | null = null;
  private cardKey: string | null = null;
  private targetKey: string | null = null;
  private targetValid: boolean | null = null;
  private drops = 0;
  private overCalls = 0;
  private hostTargetCalls = 0;

  constructor(private readonly host: DragHost) {}

  get snapshot(): DragSnapshot { return { phase: this.phase, appointmentId: this.appointmentId, cardKey: this.cardKey, targetKey: this.targetKey, targetValid: this.targetValid, drops: this.drops }; }
  /* Diagnostics for the performance guard: how many dragover events arrived vs. how many DOM updates they caused. */
  get counters(): { overCalls: number; hostTargetCalls: number } { return { overCalls: this.overCalls, hostTargetCalls: this.hostTargetCalls }; }

  /* dragstart: a stale previous drag (e.g. cancelled outside the window) is reset first, so starting is always possible. */
  start(appointmentId: string, cardKey: string): void {
    if (this.phase !== 'idle') this.reset();
    this.phase = 'dragging'; this.appointmentId = appointmentId; this.cardKey = cardKey;
    this.host.setDraggingCard(cardKey);
    this.host.setBodyDragging(true);
  }

  /* dragover: cheap — only touches the DOM when the column or validity actually changes. */
  over(columnKey: string | null, valid: boolean): void {
    this.overCalls += 1;
    if (this.phase !== 'dragging') return;
    if (columnKey === this.targetKey && valid === this.targetValid) return;
    this.targetKey = columnKey; this.targetValid = columnKey ? valid : null;
    this.hostTargetCalls += 1;
    this.host.setTarget(columnKey, valid);
  }

  /* drop: returns the appointment id to act on and resets the interaction state immediately — before the caller re-renders. */
  drop(): string | null {
    if (this.phase !== 'dragging') return null;
    const id = this.appointmentId;
    this.drops += 1;
    this.reset();
    return id;
  }

  /* dragend / escape / cancelled drag: idempotent cleanup. */
  end(): void { this.reset(); }

  private reset(): void {
    this.phase = 'idle'; this.appointmentId = null;
    this.host.setTarget(null, false);
    this.host.setDraggingCard(null);
    this.host.setBodyDragging(false);
    this.cardKey = null; this.targetKey = null; this.targetValid = null;
  }
}
